import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery, useQueries } from '@tanstack/react-query';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import Multiselect from 'multiselect-react-dropdown';
import {
  getAllRoles,
  getAllCompanies,
  getAllDesignations,
  getProjectsByCompanyId,
  getSalesManagerUsers,
  getSalesManagersForProject
} from '../../services/api/implementation/impl';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import { capitalizeWords } from '../../utils/stringUtils';

// Validation schema with added vertical field
const validationSchema = yup.object().shape({
  vertical: yup.string().required('Vertical is required'),
  designation_id: yup.number().required('Designation is required'),
  level: yup.string().required('Level is required'),
  name: yup
    .string()
    .required('Full name is required')
    .min(3, 'Full name should be at least 3 characters')
    .max(50, 'Full name should be at most 50 characters'),
  phone_number: yup
    .string()
    .required('Phone number is required')
    .matches(
      /^(\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      'Phone number is not valid'
    )
    .test(
      'len',
      'Must be a valid phone number',
      (val) => !val || (val && val.replace(/[^\d]/g, '').length === 10)
    ),
  email: yup.string().required('Email is required').email('Invalid email format'),
  // Ensures that at least one company is selected
  company_ids: yup.array().min(1, 'At least one company must be selected'),
  project_ids: yup.array().min(1, 'At least one project must be selected') // Make projects mandatory
});

export const AddUser = ({ onClose, onSubmit, users, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(validationSchema),
    // Default values now include vertical as well as the other fields
    defaultValues: {
      vertical: '',
      designation_id: '',
      level: '',
      name: '',
      phone_number: '',
      email: '',
      company_ids: [],
      project_ids: []
    }
  });

  // Local states for extra fields
  const [salesId, setSalesId] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [selectedVertical, setSelectedVertical] = useState('');
  const selectedLevel = watch('level');
  const selectedVerticalWatch = watch('vertical');
  // Watch for changes in company_ids and project_ids
  const selectedCompanies = watch('company_ids');
  const selectedProjects = watch('project_ids');

  // When vertical is Sales and level is Executive (Low), we need a sales manager per project
  const [projectManagers, setProjectManagers] = useState({});

  // Fetch data
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: getAllRoles
  });
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: getAllCompanies
  });
  const { data: designations } = useQuery({
    queryKey: ['designations'],
    queryFn: getAllDesignations
  });

  const showSalesManagerSection =
    selectedVerticalWatch === 'Sales' && selectedLevel === 'Low' && selectedProjects?.length > 0;

  const resolveProjectId = (project) =>
    Number(project?.id ?? project?.project_id ?? project?.projectId);

  const resolveCompanyId = (project) =>
    Number(project?.company_id ?? project?.companyId);

  // One API call per project so the dropdown only gets managers for that project
  const salesManagerQueries = useQueries({
    queries: (selectedProjects ?? []).map((project) => {
      const projectId = resolveProjectId(project);
      const companyId = resolveCompanyId(project);
      return {
        queryKey: ['salesManagerUsers', projectId, companyId],
        queryFn: () => getSalesManagerUsers(1, 500, projectId, companyId || null),
        enabled: showSalesManagerSection && Boolean(projectId),
        staleTime: 30 * 1000
      };
    })
  });

  const getManagersForProject = (project) => {
    const projectId = resolveProjectId(project);
    const index = (selectedProjects ?? []).findIndex(
      (item) => resolveProjectId(item) === projectId
    );
    if (index < 0) return [];
    const managers = salesManagerQueries[index]?.data?.data ?? [];
    return getSalesManagersForProject(managers, projectId);
  };

  // Keep projectManagers in sync with selected projects (add new, remove deselected)
  useEffect(() => {
    if (!selectedProjects?.length) {
      setProjectManagers({});
      return;
    }
    const projectIds = selectedProjects.map((p) => p.id);
    setProjectManagers((prev) => {
      const next = { ...prev };
      projectIds.forEach((id) => {
        if (next[id] === undefined) next[id] = '';
      });
      Object.keys(next).forEach((id) => {
        if (!projectIds.includes(Number(id))) delete next[id];
      });
      return next;
    });
  }, [selectedProjects?.map((p) => p.id).join(',')]);

  // Clear manager selection if they are no longer in the project-specific list
  useEffect(() => {
    if (!selectedProjects?.length) {
      return;
    }
    setProjectManagers((prev) => {
      let changed = false;
      const next = { ...prev };
      selectedProjects.forEach((project) => {
        const projectId = resolveProjectId(project);
        const validManagers = getManagersForProject(project);
        const selectedManagerId = Number(next[projectId]);
        if (
          selectedManagerId &&
          !validManagers.some((manager) => manager.id === selectedManagerId)
        ) {
          next[projectId] = '';
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [salesManagerQueries.map((query) => query.dataUpdatedAt).join(','), selectedProjects]);

  // Fetch projects for selected companies
  const { data: companyProjects = [] } = useQuery({
    queryKey: ['projects', selectedCompanies?.map((company) => company.id)],
    queryFn: async () => {
      if (!selectedCompanies?.length) return [];

      try {
        // Fetch projects for each selected company
        const projectPromises = selectedCompanies.map(async (company) => {
          const projects = await getProjectsByCompanyId(company.id);
          // Add company info to each project
          return projects.map((project) => ({
            ...project,
            company_name: company.name // Add company name to project data
          }));
        });

        const projectsArrays = await Promise.all(projectPromises);
        return projectsArrays.flat();
      } catch (error) {
        console.error('Error fetching projects:', error);
        return [];
      }
    },
    enabled: !!selectedCompanies?.length
  });

  // Transform projects data
  const transformedDataProjects =
    companyProjects?.map((item) => ({
      name: item.name || '',
      id: item.id,
      displayName: `${item.name || ''} (${item.company_name || ''})`,
      company_id: item.company_id
    })) || [];

  console.log('Selected Companies:', selectedCompanies);
  console.log('All Projects:', companyProjects);

  let userRoleId = Cookies.get('userRoleId');
  console.log({ userRoleId });

  // Form submission handler
  const onSubmitForm = (data) => {
    console.log({ data });
    // Destructure to exclude max_discount_limit from data
    const { max_discount_limit, ...restData } = data;

    const modifiedData = {
      ...restData,
      user_type: 'Employee',
      // Map company objects to just their IDs
      company_ids: data.company_ids.map((item) => item.id),
      project_ids: data.project_ids?.length ? data.project_ids.map((item) => item.id) : [], // Handle optional projects
      role_id: 2,
      authorized_person: true,
      level: data.level
    };

    // Only add max_discount_limit if designation is Sales (salesId equals 3)
    if (parseInt(salesId) === 3 && max_discount_limit !== '') {
      modifiedData.max_discount_limit = parseInt(max_discount_limit, 10);
    }

    // When Vertical = Sales and Level = Executive, send managers array: [{ user_id, project_id }] per project
    if (data.vertical === 'Sales' && data.level === 'Low' && data.project_ids?.length) {
      const missing = data.project_ids.filter((p) => !projectManagers[p.id] || projectManagers[p.id] === '');
      if (missing.length > 0) {
        toast.error('Please select a Sales Manager for every project.');
        return;
      }
      modifiedData.managers = data.project_ids.map((project) => ({
        user_id: Number(projectManagers[project.id]),
        project_id: project.id
      }));
    }

    console.log("onSubmitForm",{ modifiedData });
     onSubmit(modifiedData);
  };

  const transformedDataCompany = companies?.map((item) => ({
    name: item.company_name?.trim(),
    id: item.id
  }));

  // Helper to only keep digits and set phone number
  const handlePhoneNumber = useCallback(
    (val) => {
      const newVal = val.replace(/[^0-9]/g, '');
      setValue('phone_number', newVal);
    },
    [setValue]
  );

  // Helper for full name: only letters and spaces, up to 50 chars
  const handleNameChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^a-zA-Z\s]/g, '').slice(0, 50);
      setValue('name', capitalizeWords(cleanedValue));
    },
    [setValue]
  );

  // When a designation is selected, update salesId (to conditionally show max discount)
  const handleDesignationChange = (value) => {
    setSalesId(value);
  };

  // When vertical is changed, update state and reset designation field
  const handleVerticalChange = (value) => {
    setSelectedVertical(value);
    setValue('vertical', value);
    // Reset designation if vertical changes
    setValue('designation_id', '');
    setSalesId('');
  };

  // Filter the designations based on the selected vertical
  const filteredDesignations = selectedVertical
    ? designations?.filter(
        (item) => item.vertical && item.vertical.toLowerCase() === selectedVertical.toLowerCase()
      )
    : [];

  const designationOptions = [
    {
      value: '',
      label: selectedVertical ? 'Select Designation' : 'Select Vertical first',
      selected: true,
      disabled: true
    },
    ...(filteredDesignations?.map((item) => ({
      value: parseInt(item.id),
      label: item.name ? item.name.replace(/\b\w/g, (char) => char.toUpperCase()) : ''
    })) || [])
  ];

  const handleProjectManagerChange = (projectId, userId) => {
    setProjectManagers((prev) => ({ ...prev, [projectId]: userId }));
  };

  // Helper for max discount input: only numbers and a max of 100
  const handleMaxDiscountChange = (e) => {
    let inputVal = e.replace(/\D/g, ''); // remove non-digits
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Max discount cannot exceed 100');
    }
    setMaxDiscount(inputVal);
    setValue('max_discount_limit', inputVal);
  };

  return (
    <>
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">User details</span>
      </div>
      <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4 pt-6">
        {/* Row 1: Vertical & Designation */}
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Vertical'}
            isRequired
            register={register}
            register_key={'vertical'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Vertical', selected: true, disabled: true },
              { value: 'Management', label: 'Management' },
              { value: 'Sales', label: 'Sales' },
              { value: 'Operations', label: 'Operations' },
              { value: 'Accounts', label: 'Accounts' },
              { value: 'Marketing', label: 'Marketing' },
              { value: 'Inventory', label: 'Inventory' },
              { value: 'Legal', label: 'Legal' }
            ]}
            onDropdownChange={handleVerticalChange}
            errors={errors.vertical}
          />
          <FormInput
            width={48}
            label={'Designation'}
            isRequired
            register={register}
            register_key={'designation_id'}
            type={'dropdown'}
            options={designationOptions}
            onDropdownChange={handleDesignationChange}
            errors={errors.designation_id}
            disabled={selectedVertical === ''}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Designation Level'}
            isRequired
            register={register}
            register_key={'level'}
            type={'dropdown'}
            options={[
              { value: '', label: 'Select Level', selected: true, disabled: true },
              ...(selectedVertical === 'Management'
                ? [
                    { value: 'High', label: 'Admin Level' },
                    { value: 'Medium', label: 'Manager Level' },
                    { value: 'Low', label: 'Executive Level' }
                  ]
                : [
                    { value: 'Medium', label: 'Manager Level' },
                    { value: 'Low', label: 'Executive Level' }
                  ])
            ]}
            errors={errors.level}
          />

          <FormInput
            width={48}
            label={'Full Name'}
            isRequired
            register_key={'name'}
            register={register}
            type={'text'}
            onType={handleNameChange}
            errors={errors.name}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Phone Number'}
            isRequired
            register={register}
            register_key={'phone_number'}
            type={'text'}
            onType={handlePhoneNumber}
            errors={errors.phone_number}
            maxLength={10}
          />

          <FormInput
            width={48}
            label={'Email address'}
            register={register}
            isRequired
            register_key={'email'}
            type={'text'}
            errors={errors.email}
          />
        </div>

        {/* New Row: Assign Projects */}
        <div className="flex justify-between items-center w-full">
          <div className="w-[48%]">
            <label className="text-gray-600 mb-1 block">Assign Company *</label>
            <Controller
              name="company_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={transformedDataCompany || []}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    console.log('Selected companies:', selectedList);
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated companies:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="name"
                  placeholder=""
                  showCheckbox
                />
              )}
            />
            {errors.company_ids && (
              <span className="text-red-500">{errors.company_ids.message}</span>
            )}
          </div>
          <div className="w-[48%]">
            <label className="text-gray-600 mb-1 block">Assign Projects *</label>
            <Controller
              name="project_ids"
              control={control}
              render={({ field }) => (
                <Multiselect
                  options={transformedDataProjects}
                  selectedValues={field.value}
                  onSelect={(selectedList) => {
                    field.onChange(selectedList);
                  }}
                  onRemove={(selectedList) => {
                    console.log('Updated projects:', selectedList);
                    field.onChange(selectedList);
                  }}
                  displayValue="displayName"
                  placeholder=""
                  showCheckbox
                  disable={!selectedCompanies?.length}
                  emptyRecordMsg={
                    !selectedCompanies?.length ? 'Select companies first' : 'No projects available'
                  }
                />
              )}
            />
            {errors.project_ids && (
              <span className="text-red-500">{errors.project_ids.message}</span>
            )}
          </div>
        </div>

        {/* Sales Executive: Sales Manager per project (Vertical = Sales, Level = Executive) */}
        {selectedVerticalWatch === 'Sales' && selectedLevel === 'Low' && selectedProjects?.length > 0 && (
          <div className="w-full border-t border-stone-200 pt-4 mt-2">
            <div className="flex flex-col gap-2 mb-3">
              <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2 text-gray-700">
                Sales Manager per project
              </span>
              <p className="text-sm text-gray-500">Select a Sales Manager for each assigned project.</p>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-md p-4 max-h-[240px] overflow-y-auto">
              <div className="flex flex-col gap-3">
                {selectedProjects.map((project, index) => (
                  <div
                    key={project.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-white rounded border border-stone-100 shadow-sm"
                  >
                    <label className="text-gray-600 text-sm font-medium shrink-0 sm:w-[42%] sm:min-w-[200px]">
                      {index + 1}. {project.displayName || project.name}
                    </label>
                    <div className="flex-1 min-w-0">
                      <select
                        className="w-full p-2.5 border border-stone-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#346C75] focus:border-transparent transition-all"
                        value={projectManagers[project.id] ?? ''}
                        onChange={(e) =>
                          handleProjectManagerChange(project.id, e.target.value ? Number(e.target.value) : '')
                        }
                        required
                      >
                        <option value="" disabled>
                          Select Sales Manager
                        </option>
                        {getManagersForProject(project).map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name || u.email || `User ${u.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center w-full">
          {parseInt(salesId) === 3 && (
            <FormInput
              width={48}
              label={'Max Discount (%)'}
              register={register}
              register_key={'max_discount_limit'}
              type={'text'}
              onType={handleMaxDiscountChange}
              defaultValuePre={maxDiscount}
              errors={errors.max_discount_limit}
            />
          )}
        </div>

        {/* Action Buttons */}
        <div className="w-full flex justify-end items-center space-x-4 mt-8">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            Add
          </Button>
        </div>
      </form>
    </>
  );
};
